import axiosInstance from '../utils/AxiosConfig';

/**
 * Calcula precios con descuento. El backend devuelve envuelto:
 * { error: false, status_code: 200, data: { data: [...], calculatedDiscountTotal: N } }
 * La lista de productos está en data.data y el total en data.calculatedDiscountTotal.
 */
export const calculateDiscountedPrices = async (data) => {
    try {
      const response = await axiosInstance.post(`/sales/calculate`, data);
      const raw = response.data;
      // Doble anidación: raw.data = { data: [...], calculatedDiscountTotal }
      const payload = raw?.data ?? raw;
      const products = Array.isArray(payload)
        ? payload
        : (Array.isArray(payload?.data) ? payload.data : []);
      const total = typeof payload?.calculatedDiscountTotal === 'number'
        ? payload.calculatedDiscountTotal
        : (typeof payload?.calculated_discount_total === 'number' ? payload.calculated_discount_total : undefined);
      return {
        data: products,
        calculatedDiscountTotal: total
      };
    } catch (error) {
      console.error('Error calculating discounted prices:', error);
      return { data: [], calculatedDiscountTotal: undefined };
    }
};

export const createInvoice = async (data) => {
  try {
    const response = await axiosInstance.post('/sales/invoice', data);
    return response.data.data;
  } catch (error) {
    console.error('Error creating invoice:', error);
    throw error;
  }
};

export const cancelInvoice = async (invoiceId) => {
  try {
    const response = await axiosInstance.delete(`/sales/invoice/${invoiceId}`);
    return response.data?.data ?? response.data;
  } catch (error) {
    console.error('Error canceling invoice:', error);
    throw error;
  }
};

export const getReturnableInvoice = async (invoiceId) => {
  const response = await axiosInstance.get(`/sales/returns/invoice/${invoiceId}`);
  return response.data?.data ?? response.data;
};

export const createSalesReturn = async (payload) => {
  const response = await axiosInstance.post('/sales/returns', payload);
  return response.data?.data ?? response.data;
};

export const getStoreCredit = async (code) => {
  const response = await axiosInstance.get(`/sales/returns/credit/${encodeURIComponent(code)}`);
  return response.data?.data ?? response.data;
};

export const fetchInvoiceTickets = async (invoiceId, options = {}) => {
  try {
    const response = await axiosInstance.get(`/sales/invoice/tickets/${invoiceId}`, {
      skipGlobalErrorRedirect: options.skipGlobalErrorRedirect === true,
    });
    return response.data;
  } catch (error) {
    console.error('Error al obtener el ticket de la invoice:', error);
    return null;
  }
};

/**
 * Obtiene el PDF de la factura desde el proveedor de facturación electrónica.
 * @param externalDocumentId ID del comprobante en el proveedor
 * @param rut RUT de la empresa (opcional; en facturas emitidas conviene pasarlo para resolver credenciales)
 */
export const fetchFiscalTicket = async (externalDocumentId, rut = null, options = {}) => {
  try {
    const url = rut
      ? `/sales/invoice/ticket/from_external_document_id/${externalDocumentId}?rut=${encodeURIComponent(rut)}`
      : `/sales/invoice/ticket/from_external_document_id/${externalDocumentId}`;
    const response = await axiosInstance.get(url, {
      responseType: 'blob',
      skipGlobalErrorRedirect: options.skipGlobalErrorRedirect === true,
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching ticket:', error);
    return null;
  }
};

export const getInvoiceStatus = async (invoiceId) => {
  try {
    const response = await axiosInstance.get(`/sales/invoice_status/${invoiceId}`);
    return response.data.data;
  } catch (error) {
    console.error('Error calculating discounted prices:', error);
    return [];
  }
};

export const getClient = async (ci) => {
  try {
    const response = await axiosInstance.get(`/clients/${ci}`);
    return response.data.data;
  } catch (error) {
    console.error('Error calculating discounted prices:', error);
    return [];
  }
};

export const getCompany = async (rut) => {
  try {
    const response = await axiosInstance.get(`/clients/companies/${rut}`);
    return response.data.data;
  } catch (error) {
    console.error('Error calculating discounted prices:', error);
    return [];
  }
};

export const lookupCompany = async (rut, locationId) => {
  try {
    const response = await axiosInstance.get(`/clients/companies/${rut}/lookup`, {
      params: { locationId }
    });
    return response.data.data;
  } catch (error) {
    console.error('Error looking up company:', error);
    throw error;
  }
};

export const resolveUndefinedInvoice = async (data) => {
  try {
    const response = await axiosInstance.post('/sales/invoice/resolve-undefined', data);
    return response.data.data;
  } catch (error) {
    console.error('Error calculating discounted prices:', error);
    return [];
  }
};

export const checkPendingInvoice = async (locationId, terminalCode) => {
  try {
    const response = await axiosInstance.get(
      `/sales/invoice/pending?locationId=${locationId}&terminalCode=${terminalCode}`
    );
    return response.data.data;
  } catch (error) {
    console.error('Error al obtener la invoice pendiente:', error);
    return null;
  }
};

export const getInvoices = async ({
  year, month, page = 0, pageSize = 10, search = '', rut, live = false, locationIds, cfeTypes,
}) => {
  try {
    const response = await axiosInstance.get(`/sales/invoices`, {
      params: {
        year,
        month,
        page,
        pageSize,
        search,
        rut,
        live,
        locationIds: locationIds && locationIds.length ? locationIds.join(',') : undefined,
        cfeTypes: cfeTypes && cfeTypes.length ? cfeTypes.join(',') : undefined,
      },
    });

    // Los datos están directamente en response.data, no en response.data.data
    const data = response.data;
    
    // Verificar que tenga la estructura esperada
    if (data && typeof data === 'object' && Array.isArray(data.invoices)) {
      const invoices = data.invoices;
      const returnedCount = data.totalCount || 0;
      
      // Calcular el total real basado en la página actual
      let totalCount;
      
      if (invoices.length === pageSize) {
        // Si devolvió exactamente lo que pedimos, probablemente hay más páginas
        // Total = (página actual + 1) * pageSize + 1 (para indicar que hay más)
        totalCount = (page + 1) * pageSize + 1;
      } else {
        // Si es menor que pageSize, es la última página
        // Total = página actual * pageSize + cantidad de items en esta página
        totalCount = page * pageSize + invoices.length;
      }
      
      return {
        invoices,
        totalCount,
        source: data.source || 'cache',
        lastUpdatedAt: data.lastUpdatedAt || null
      };
    }

    // Si no tiene la estructura esperada, retornar estructura por defecto
    return { invoices: [], totalCount: 0, source: 'empty', lastUpdatedAt: null };
  } catch (error) {
    console.error('Error fetching invoices:', error);
    return { invoices: [], totalCount: 0, source: 'error', lastUpdatedAt: null };
  }
};

export const syncSentInvoicesCache = async () => {
  const response = await axiosInstance.post('/sales/invoices/sync-cache');
  return response.data?.data ?? response.data;
};

export const getInvoiceBranches = async (rut) => {
  try {
    const response = await axiosInstance.get('/sales/invoices/branches', { params: { rut } });
    const branches = response.data?.data ?? response.data;
    return Array.isArray(branches) ? branches : [];
  } catch (error) {
    console.error('Error fetching invoice branches:', error);
    return [];
  }
};

export const getInvoice = async (serie, number) => {
  try {
    const response = await axiosInstance.get(`/invoice/${serie}/${number}`);
    return response.data.data;
  } catch (error) {
    console.error('Error fetching invoice:', error);
    return null;
  }
};

export const createCustomInvoice = async (data) => {
  try {
    const response = await axiosInstance.post('/invoice/custom', data);
    return response.data.data;
  } catch (error) {
    console.error('Error creando factura:', error);
    return null;
  }
};

export const fetchInvoiceDetails = async (invoiceId, options = {}) => {
  const response = await axiosInstance.get(`/sales/invoice/${invoiceId}`, {
    skipGlobalErrorRedirect: options.skipGlobalErrorRedirect === true,
  });
  // Backend suele devolver { data: invoice }; soportar también respuesta directa
  return response.data?.data ?? response.data;
};

export const getAllTransactions = async (page = 1, pageSize = 10, searchTerm = '', options = {}) => {
  try {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString()
    });
    
    if (searchTerm && typeof searchTerm === 'string' && searchTerm.trim()) {
      params.append('search', searchTerm.trim());
    }
    if (options.sortField) params.append('sortField', options.sortField);
    if (options.sortDirection) params.append('sortDirection', options.sortDirection);
    if (options.filterField) params.append('filterField', options.filterField);
    if (options.filterOperator) params.append('filterOperator', options.filterOperator);
    if (options.filterValue !== undefined && options.filterValue !== null && options.filterValue !== '') {
      params.append('filterValue', options.filterValue.toString());
    }
    
    const response = await axiosInstance.get(`/sales/all_transactions?${params}`);
    return response.data.data;
  } catch (error) {
    console.error('Error al obtener todas las transacciones:', error);
    return [];
  }
};

export const getAllTransactionsBySeller = async (locationId, terminalCode, page = 1, pageSize = 10, searchTerm = '', options = {}) => {
  try {
    const params = new URLSearchParams({
      locationId: locationId.toString(),
      terminalCode: terminalCode.toString(),
      page: page.toString(),
      pageSize: pageSize.toString()
    });
    
    if (searchTerm && typeof searchTerm === 'string' && searchTerm.trim()) {
      params.append('search', searchTerm.trim());
    }
    if (options.sortField) params.append('sortField', options.sortField);
    if (options.sortDirection) params.append('sortDirection', options.sortDirection);
    if (options.filterField) params.append('filterField', options.filterField);
    if (options.filterOperator) params.append('filterOperator', options.filterOperator);
    if (options.filterValue !== undefined && options.filterValue !== null && options.filterValue !== '') {
      params.append('filterValue', options.filterValue.toString());
    }
    
    const response = await axiosInstance.get(`/sales/transactions_by_seller?${params}`);
    return response.data.data;
  } catch (error) {
    console.error('Error al obtener transacciones del vendedor:', error);
    return [];
  }
};

/**
 * @param {string} externalDocumentId - ID del comprobante en el proveedor
 * @param {boolean} fromProvider - Si true, el backend trae el PDF directamente del proveedor (para facturas enviadas)
 */
export const getInvoicePDF = async (externalDocumentId, fromProvider = false) => {
  try {
    const url = fromProvider ? `/invoice/pdf/${externalDocumentId}?from=provider` : `/invoice/pdf/${externalDocumentId}`;
    const response = await axiosInstance.get(url, {
      responseType: 'blob', // Importante para archivos PDF
    });
    return response.data;
  } catch (error) {
    console.error('Error al obtener PDF de la factura:', error);
    throw error;
  }
};
